import type { ReactNode } from 'react';
import { useLocation, useRouteLoaderData } from 'react-router';

import type { TContentGatePopupElement } from '@shared/dream-components';
import { ContentGatePopupElement } from '@shared/dream-components';

import { usePostPageContext } from '~/context/usePostPageContext';
import useClipContentHeight from '~/hooks/useClipContentHeight';
import useContentGateScroll from '~/hooks/useContentGateScroll';
import usePostContentBlur from '~/hooks/usePostContentBlur';
import type { RootLoaderData } from '~/root';

import { processContentGatePopupNode } from '../utils/processors';

const CLIP_HEIGHT = 200;

interface Props {
  node: TContentGatePopupElement;
  children: ReactNode | ReactNode[];
}

const ContentGatePopup = ({ node, children }: Props) => {
  const { publication } = useRouteLoaderData('root') as RootLoaderData;
  const { isSubscriberGateEnabled } = usePostPageContext() || { isSubscriberGateEnabled: false };
  const { pathname } = useLocation();

  useClipContentHeight({ enabled: isSubscriberGateEnabled, scrollHeight: CLIP_HEIGHT, navigationKey: pathname });
  const showPopup = useContentGateScroll({
    enabled: isSubscriberGateEnabled,
    clipHeight: CLIP_HEIGHT,
    navigationKey: pathname,
  });
  usePostContentBlur(showPopup);

  if (!isSubscriberGateEnabled) {
    return null;
  }

  const processedNode = node ? processContentGatePopupNode(node, { publicationUrl: publication?.url }) : node;

  const overlayColor = processedNode?.attrs?.modalOverlayBackgroundColor || 'rgba(255, 255, 255, 0.9)';

  return (
    <>
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          height: '180px',
          background: `linear-gradient(to bottom, transparent 0%, ${overlayColor} 85%)`,
          pointerEvents: 'none',
          zIndex: 39,
          opacity: showPopup ? 1 : 0,
          transition: 'opacity 0.8s cubic-bezier(0.32, 0.72, 0, 1)',
        }}
      />
      <ContentGatePopupElement element={processedNode} isLive isOpen={showPopup}>
        {children}
      </ContentGatePopupElement>
    </>
  );
};

export { ContentGatePopup };
